<script setup lang="ts">
import '@gohighlevel/ghl-ui/dist/style.css'
import {
  UIContentWrap,
  UIButton,
  UIToolbar,
  UIToolbarGroup,
  UIActionDropdown,
} from '@gohighlevel/ghl-ui'
import {
  EyeIcon,
  XCloseIcon,
  Edit01Icon,
  ArrowLeftIcon,
  Send03Icon,
  UserCircleIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { useRoute } from 'vue-router'
import { builderState } from './../composable/manageBuilderState'
import { debounce } from 'lodash'
import { computed, ref, watch, watchEffect } from 'vue'
import i18n from 'common/locales'
import { appState } from '../composable/appState'

const route = useRoute()

const currentPageName = ref<string>( route?.name )
if(!currentPageName.value && appState.isEmailPreviewApp) {
  currentPageName.value = appState.selectedPage
}

const routeName = computed( () => route?.name)

watch(
  () => appState.selectedPage,
  (newVal, oldVal) => {
    if (newVal) currentPageName.value = newVal as string
  }
)

watchEffect(() => {
    if (routeName.value) currentPageName.value = routeName.value as string
  }
)

const headerMenuOptions = computed(() =>
  props?.menuOptions?.map(item => ({
    label: item?.name,
    key: item?.id,
    icon: item?.newIcon,
  }))
)
const isBuilder = computed(
  () => currentPageName.value === 'builder' || currentPageName.value == 'email_edit_campaign'
)
const saveTitle = computed(() =>
  props.isModal || currentPageName.value === 'email_edit_campaign'
    ? i18n.global.t('common.save')
    : i18n.global.t('emailTemplate.saveTemplate')
)
const editTitle = ref(false)
const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: 'Email Template',
  },
  unsavedChanges: {
    type: Boolean,
    default: false,
  },
  contentEditable: {
    type: Boolean,
    default: false,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
  backTitle: {
    type: String,
    default: 'Back',
  },
  menuOptions: {
    type: Array<any>,
    default: [],
  },
  disableRestore: {
    type: Boolean,
    default: false,
  },
  showCloneTemplate: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits([
  'model:close',
  'title:update',
  'route:back',
  'menu:click',
  'save:click',
  'version:restore',
  'schedule:click',
  'saveAsTemplate:click',
  'contact:click',
])

const handleTemplateTitle = debounce(async (e: any) => {
  if (e.target.innerText?.trim()) {
    emit('title:update', e.target.innerText)
  }
}, 1000)

const handleClick = (task: string, key?: string) => {
  switch (task) {
    case 'back':
      emit('route:back')
      break
    case 'close':
      emit('model:close')
      break
    case 'preview':
      emit('menu:click', {
        id: 'preview_campaign',
      })
      break
    case 'menu':
      emit('menu:click', { id: key })
      break
    case 'save':
      emit('save:click')
      break
    case 'restore':
      emit('version:restore')
      break
    case 'schedule':
      emit('schedule:click')
      break
    case 'saveAsTemplate':
      emit('saveAsTemplate:click')
      break
    case 'contact-open':
      emit('contact:click')
      break
    default:
      break
  }
}

// refs for modal header title and normal header title
const titleInput = ref()
const modalTitle = ref()

const handleEnterPress = (e: KeyboardEvent) => {
  // while pressing enter blur the title focus
  if (e.key === 'Enter') {
    if (titleInput.value) titleInput.value?.blur()
    if (modalTitle.value) modalTitle.value?.blur()
  }
}
</script>

<template>
  <UIContentWrap fullScreen>
    <UIToolbar theme="light" class="h-14 hl-toolbar fonts-fallback">
      <!-- items on left -->
      <UIToolbarGroup borderRight class="h-full">
        <!-- back button for modal -->
        <UIButton
          v-if="!isModal"
          text
          id="back-btn"
          @click="handleClick('back')"
          class="text-black h-full flex items-center"
          ><ArrowLeftIcon class="h-4 w-4 margin-r-3" />{{ backTitle }}</UIButton
        >
        <!-- title for modal -->
        <div
          v-if="title && isModal"
          class="flex text-black items-center h-full modal-title"
        >
          <div
            :contenteditable="isBuilder && contentEditable"
            :class="`text-lg ${!editTitle ? 'truncate max-w-lg' : ''}`"
            ref="modalTitle"
            @input="handleTemplateTitle"
            @click="editTitle = true"
            @focusout="editTitle = false"
            @keydown="handleEnterPress"
          >
            {{ title }}
          </div>
          <Edit01Icon
            @click="$refs?.modalTitle?.focus()"
            v-if="isBuilder && contentEditable"
            class="w-5 h-5 ml-2 text-gray-700 hover:text-gray-500"
          />
        </div>
      </UIToolbarGroup>
      <!-- items on center -->
      <UIToolbarGroup class="flex items-center">
        <!-- title of builder (not modal) -->
        <div
          v-if="
            title &&
            !isModal &&
            (builderState.previewUrl || builderState.unsavedChanges)
          "
          class="flex items-center text-black toolbar-center-item normal-title"
        >
          <div
            :contenteditable="contentEditable"
            :class="`text-lg ${!editTitle ? 'truncate max-w-lg' : ''}`"
            @input="handleTemplateTitle"
            @click="editTitle = true"
            @focusout="editTitle = false"
            @keydown="handleEnterPress"
            ref="titleInput"
          >
            {{ title }}
          </div>
          <Edit01Icon
            @click="$refs?.titleInput?.focus()"
            v-if="contentEditable"
            class="w-5 h-5 ml-2 text-gray-700 hover:text-gray-500"
          />
        </div>
      </UIToolbarGroup>
      <!-- items on right -->
      <UIToolbarGroup
        v-if="!builderState?.mediaOpen"
        borderLeft
        class="flex items-center justify-end"
      >
        <!-- preview button -->
        <UIButton
          id="preview-btn"
          @click="handleClick('preview')"
          v-if="currentPageName !== 'email_preview_template' && isModal"
          :disabled="builderState.loader"
          ><EyeIcon class="w-5 h-5 mr-2" /> {{ $t('common.preview') }}</UIButton
        >
        <!-- menu action button -->
        <UIActionDropdown
          :id="`${id}-email-builder-action-menu`"
          v-if="isBuilder && !isModal && builderState.previewUrl"
          :options="headerMenuOptions"
          @select="val => handleClick('menu', val)"
          placement="bottom"
        />

        <!-- edit button for modal -->
        <UIButton
          id="edit-btn"
          type="primary"
          v-if="currentPageName === 'email_preview_template' && isModal"
          @click="handleClick('back')"
          ><Edit01Icon class="w-5 h-5 mr-2" /> {{ $t('common.edit') }}</UIButton
        >
        <!-- save buttons -->

        <UIButton
          id="save-as-new-template-btn"
          @click="handleClick('saveAsTemplate')"
          v-if="
            currentPageName !== 'email_preview_template' &&
            isModal &&
            showCloneTemplate
          "
          :disabled="builderState.saveAsTemplateLoading || !builderState.previewUrl"
          :loading="builderState.saveAsTemplateLoading"
          >{{ $t('emailTemplate.saveCreateNewTemplate') }}</UIButton
        >
        <UIButton
          id="save-btn"
          v-if="isBuilder"
          @click="handleClick('save')"
          :disabled="!builderState.previewUrl && !builderState.unsavedChanges"
          :loading="builderState.loader"
        >
          <span
            class="flex absolute h-3 w-3 top-0 right-0 -mt-1 -mr-1 z-10"
            v-if="builderState.unsavedChanges && !builderState.loader"
          >
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-600 opacity-75"
            ></span>
            <span
              class="relative inline-flex rounded-full h-3 w-3 bg-yellow-600"
            ></span>
          </span>
          {{ saveTitle }}
        </UIButton>
        <!-- schedule button -->
        <UIButton
          id="send-or-schedule-btn"
          type="primary"
          v-if="currentPageName === 'email_edit_campaign'"
          :disabled="!builderState.previewUrl && !builderState.unsavedChanges"
          @click="handleClick('schedule')"
          ><Send03Icon class="h-5 w-5 mr-2" />
          {{ $t('emailCampaign.sendOrSchedule') }}</UIButton
        >
        <!-- preview contact button -->
        <UIButton
          id="pg-email-builder-header__preview-contact-btn"
          v-if="
            ['email_preview_template', 'email_preview_campaign'].includes(
              currentPageName
            )
          "
          @click="handleClick('contact-open')"
        >
          <UserCircleIcon class="w-5 h-5 mr-1" />
          {{ $t('common.previewContactInfo') }}
        </UIButton>
        <!-- close icon for modal -->
        <UIButton
          v-if="isModal"
          id="close-icon"
          class="h-full items-center focus:outline-none"
          text
          @click="handleClick('close')"
        >
          <XCloseIcon class="w-5 h-5 text-gray-700 hover:text-gray-500" />
        </UIButton>
        <!-- restore button -->
        <UIButton
          id="restore-btn"
          v-if="
            currentPageName === 'email_template_version' ||
            currentPageName === 'email_campaign_versions'
          "
          :loading="builderState.loader"
          :disabled="disableRestore"
          @click="handleClick('restore')"
          >{{ $t('common.restoreVersion') }}</UIButton
        >
        <!-- test mail button - schedule page -->
        <UIButton
          id="pg-email-builder-schedule-screen__test-email-btn"
          v-if="currentPageName === 'email_schedule_campaign'"
          :disabled="!builderState.previewUrl"
          @click="handleClick('menu', 'test_email')"
          >{{ $t('common.sendTestEmail') }}</UIButton
        >
      </UIToolbarGroup>
    </UIToolbar>
  </UIContentWrap>
</template>
<style lang="scss">
.toolbar-center-item {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}
.modal-title,
.normal-title {
  div {
    min-width: 1rem;
  }
  svg {
    cursor: pointer;
  }
}
#emailPreview .hl-toolbar {
  background-color: transparent !important;
  padding: 0 !important;
}
#email-preview-email-builder-action-menu-action-dropdown-trigger {
  display: inline-flex;
  height: 34px;
  width: 34px;
}

.margin-r-3 {
  margin-right: .75rem;
}
.v-binder-follower-content:has(#email-preview-email-builder-action-menu) {
  transform: translateX(calc(100vw - 185px)) translateY(45px) translateX(-50%) !important;
  transform-origin: center top !important;
  margin-top: 4px;
}
</style>
